import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import './index.css';
import { CACHE_BUSTER, PERSIST_MAX_AGE, persister, queryClient } from './lib/queryClient';

/**
 * New version after a redeploy: switch to it only while nothing is being edited – no open sheet
 * or dialog and no save in flight – so a half-filled form is never wiped by the reload.
 */
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    const idle = () =>
      document.querySelector('dialog[open]') === null && queryClient.isMutating() === 0;
    const apply = () => {
      if (!idle()) return false;
      void updateSW(true);
      return true;
    };
    if (apply()) return;
    const timer = window.setInterval(() => {
      if (apply()) window.clearInterval(timer);
    }, 3000);
  },
});

// Installed app: ask the browser never to evict its storage (unsent check-ins live in IndexedDB).
// Only when installed – there it is granted silently, in a tab Firefox would ask every user.
if (window.matchMedia('(display-mode: standalone)').matches && 'storage' in navigator) {
  void navigator.storage
    .persisted()
    .then((done) => (done ? true : navigator.storage.persist()))
    .catch(() => false);
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');

createRoot(root).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
        buster: CACHE_BUSTER,
        dehydrateOptions: {
          // Remember only a positive session (so the app opens offline); "logged out" is never cached.
          shouldDehydrateQuery: (q) =>
            q.state.status === 'success' &&
            (q.queryKey[0] !== 'session' ||
              (q.state.data as { authenticated?: boolean } | undefined)?.authenticated === true),
        },
      }}
      // The saved copy only bridges the start (and offline use): always ask the server for the
      // current state right after, so a change made just before closing never looks lost.
      onSuccess={() => void queryClient.invalidateQueries()}
    >
      <App />
    </PersistQueryClientProvider>
  </StrictMode>,
);
